import { useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { ChatSessionModel, MessageVisibilityMode } from "@/api/models"
import type { ChatStore } from "@/features/chat/hooks/chat-request-registry"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { EyeOff, Loader2, Share2, UserMinus, UserPlus } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { useChatSessionQueryScope } from "@/features/chat/hooks/use-chat-session-query-scope"
import {
  useAddSessionParticipant,
  useLeaveSharedSession,
  useRemoveSessionParticipant,
  useSessionParticipants,
  useTransferSessionOwnership,
  useUpdateParticipantAccessMode,
} from "@/hooks/use-session-sharing"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"

import {
  getSharedSessionAccessState,
  getSharedSessionActionLabel,
  getSharedSessionConfirmDescription,
  getSharedSessionConfirmTitle,
  showSharedSessionActionErrorToast,
  showSharedSessionActionSuccessToast,
} from "./shared-session-leave-utils"
import { ParticipantList } from "../../projects/components/participant-list"

type ChatShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  session: ChatSessionModel | undefined
  currentMessageId?: string // ID of current/latest message in chat
  onLeave?: () => void
  // The chat store the session belongs to: the latest message id is read from
  // the session's current query identity, never from a deleted chat (#3524).
  chatStore: ChatStore
}

export function ChatShareDialog({
  open,
  onOpenChange,
  sessionId,
  session,
  currentMessageId,
  onLeave,
  chatStore,
}: ChatShareDialogProps) {
  const { t } = useTranslation()

  const settingsService = SettingsService.getInstance()
  const currentUserOid = settingsService.claims.oid
  const currentUserGroups = (settingsService.claims.groups ?? []).filter((g): g is string =>
    Boolean(g)
  )
  const currentUserGroupSet = new Set(currentUserGroups)

  const { data: participantsData, isLoading } = useSessionParticipants(sessionId, open)

  // Fetch messages from backend when dialog opens to get latest message ID
  const queryScope = useChatSessionQueryScope(chatStore, sessionId)
  const { data: backendMessages } = useQuery({
    queryKey: queryKeys.sessionMessagesQuery(sessionId, undefined, queryScope.incarnation),
    queryFn: async () => {
      const chatService = ChatService.getInstance()
      return await chatService.getMessages(sessionId)
    },
    enabled: open && !!sessionId && !queryScope.disposed,
    staleTime: 1000 * 30, // Consider fresh for 30 seconds
  })

  // Extract latest message ID from backend messages
  const latestMessageId = useMemo(() => {
    if (backendMessages && backendMessages.length > 0) {
      // Get the most recent message with a valid ID
      const latestMessage = backendMessages[backendMessages.length - 1]
      if (latestMessage.id) {
        return latestMessage.id
      }
    }
    return currentMessageId
  }, [backendMessages, currentMessageId])

  const addParticipant = useAddSessionParticipant()
  const removeParticipant = useRemoveSessionParticipant()
  const updateAccessMode = useUpdateParticipantAccessMode()
  const transferOwnership = useTransferSessionOwnership()
  const leaveSharedSession = useLeaveSharedSession()

  const [showLeaveDialog, setShowLeaveDialog] = useState(false)

  // State for message visibility mode
  const [messageAccessMode, setMessageAccessMode] = useState<MessageVisibilityMode>("full_history")

  // Effect to detect when a specific message is selected and set the mode accordingly
  useEffect(() => {
    if (open && currentMessageId) {
      // A specific message was explicitly selected via "Share message" button
      setMessageAccessMode("specific_message")
    } else if (open && !currentMessageId) {
      // Dialog opened without a specific message (e.g., from share button in header)
      setMessageAccessMode("full_history")
    }
    // Reset when dialog closes
    if (!open) {
      setMessageAccessMode("full_history")
    }
  }, [open, currentMessageId])

  // Allow management if user is owner (either via currentUserRole or by checking userId)
  const canManage =
    session?.currentUserRole === "owner" ||
    (!session?.currentUserRole && session?.userId === currentUserOid)

  const participants = participantsData?.participants ?? []
  const { hasDirectShare, hasGroupShare } = getSharedSessionAccessState(
    participants.map((participant) => ({
      oid: participant.oid,
      type: participant.type,
    })),
    currentUserOid,
    currentUserGroupSet
  )
  const canLeaveOrHide =
    !canManage && session?.currentUserRole === "reader" && (hasDirectShare || hasGroupShare)

  const leaveActionLabel = getSharedSessionActionLabel(t, hasDirectShare)

  const handleConfirmLeave = async () => {
    try {
      const result = await leaveSharedSession.mutateAsync({ sessionId })
      showSharedSessionActionSuccessToast(t, result.action)

      setShowLeaveDialog(false)
      onOpenChange(false)
      onLeave?.()
    } catch {
      showSharedSessionActionErrorToast(t)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const existingOids = (participantsData?.participants?.map((p) => p.oid) ?? []).filter(
    Boolean
  ) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) {
      return
    }

    // Use currentMessageId if provided and mode is not full_history, otherwise use latestMessageId
    const messageIdToUse = currentMessageId || latestMessageId
    const sharedFromMessageId = messageAccessMode === "full_history" ? undefined : messageIdToUse

    await addParticipant.mutateAsync({
      sessionId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: "reader",
        messageAccessMode,
        sharedFromMessageId,
      },
    })

    // Reset to default after adding
    setMessageAccessMode("full_history")
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({
      sessionId,
      participantOid,
    })
  }

  const handleAccessModeChange = async (
    participantOid: string,
    mode: MessageVisibilityMode,
    messageId?: string
  ) => {
    await updateAccessMode.mutateAsync({
      sessionId,
      participantOid,
      messageAccessMode: mode,
      sharedFromMessageId: messageId,
    })
  }

  const handleTransferOwnership = async (newOwnerOid: string) => {
    await transferOwnership.mutateAsync({
      sessionId,
      newOwnerOid,
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="size-5" />
              {t("sharing.dialog_title_chat", { defaultValue: "Share Chat" })}
            </DialogTitle>
            <DialogDescription>
              {t("sharing.dialog_description_chat", {
                defaultValue:
                  "Add users or groups to view this chat. Readers can view but cannot add messages.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {canManage && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="text-muted-foreground size-4" />
                  <Label className="text-sm font-medium">
                    {t("sharing.add_participant", { defaultValue: "Add People" })}
                  </Label>
                </div>

                <DirectorySearch
                  disabled={addParticipant.isPending}
                  selectedOids={existingOids}
                  onSelect={handleAddParticipant}
                />

                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("sharing.message_access_mode", { defaultValue: "Message visibility" })}
                  </Label>
                  <Select
                    value={messageAccessMode}
                    onValueChange={(value) => setMessageAccessMode(value as MessageVisibilityMode)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_history">
                        {t("sharing.access_full_history")}
                      </SelectItem>
                      <SelectItem disabled={!latestMessageId} value="partial_history">
                        {t("sharing.access_partial_history")}
                      </SelectItem>
                      <SelectItem disabled={!latestMessageId} value="specific_message">
                        {t("sharing.access_specific_message")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {currentMessageId && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground text-xs">
                        {t("sharing.specific_message_selected")}
                      </p>
                      {messageAccessMode !== "full_history" && (
                        <p className="text-muted-foreground text-xs">
                          Message ID:{" "}
                          <code className="bg-muted rounded px-1 py-0.5">{currentMessageId}</code>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <Separator />
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t("sharing.participants", { defaultValue: "People with Access" })}
              </Label>
              <ParticipantList
                allowedRoles={["reader"]}
                canManage={canManage}
                currentUserOid={currentUserOid}
                entityType="chat"
                isCurrentUserOwner={session?.currentUserRole === "owner"}
                isLoading={isLoading}
                latestMessageId={latestMessageId}
                ownerUserId={participantsData?.ownerUserId}
                participants={participantsData?.participants ?? []}
                selectedMessageId={currentMessageId}
                showAccessMode={true}
                onAccessModeChange={handleAccessModeChange}
                onRemove={handleRemove}
                onRoleChange={undefined}
                onTransferOwnership={handleTransferOwnership}
              />
            </div>

            <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
              <p className="mb-1 font-medium">
                {t("sharing.role_descriptions_title", { defaultValue: "Permission levels:" })}
              </p>
              <ul className="space-y-0.5">
                <li>
                  <strong>{t("sharing.role_reader", { defaultValue: "Reader" })}:</strong>{" "}
                  {t("sharing.role_reader_chat_desc", {
                    defaultValue: "Can view chat messages but cannot add new messages",
                  })}
                </li>
                <li>
                  <strong>{t("sharing.role_owner", { defaultValue: "Owner" })}:</strong>{" "}
                  {t("sharing.role_owner_chat_desc", {
                    defaultValue: "Full control including managing access and adding messages",
                  })}
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {canLeaveOrHide ? (
              <Button
                className="text-destructive hover:text-destructive gap-2"
                disabled={leaveSharedSession.isPending}
                variant="ghost"
                onClick={() => setShowLeaveDialog(true)}
              >
                {hasDirectShare ? <UserMinus className="size-4" /> : <EyeOff className="size-4" />}
                {leaveActionLabel}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getSharedSessionConfirmTitle(t, hasDirectShare)}</AlertDialogTitle>
            <AlertDialogDescription>
              {getSharedSessionConfirmDescription(t, hasDirectShare, hasGroupShare)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction
              disabled={leaveSharedSession.isPending}
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmLeave()
              }}
            >
              {leaveSharedSession.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {leaveActionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
